import { createFileRoute, Outlet, redirect, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { meApi } from "@/api/me";
import { authApi } from "@/api/auth";
import { useAuthStore } from "@/auth/store";
import { ensureSession } from "@/auth/refresh-loop";
import { useState } from "react";
import { FolderKanban, KeyRound, BarChart3, ShieldCheck, LogOut, User, BookOpen, Sun, Moon } from "lucide-react";
import { Wordmark } from "@/components/ui/Wordmark";

export const Route = createFileRoute("/app")({
  beforeLoad: async () => {
    if (!(await ensureSession())) throw redirect({ to: "/login" });
  },
  component: AppShell,
});

function AppShell() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);

  // Light hydration of /me on shell mount — populates role + ensures
  // memberships are fresh. Children re-use the same query.
  useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const me = await meApi.get();
      setUser(me.user);
      return me;
    },
  });

  async function logout() {
    try { await authApi.logout(); } catch { /* still clear locally */ }
    useAuthStore.getState().clear();
    navigate({ to: "/login", replace: true });
  }

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-64 shrink-0 border-r border-ink/8 bg-cream/60 px-4 py-6 backdrop-blur-xl md:flex md:flex-col">
        <div className="px-2">
          <Wordmark />
        </div>

        <nav className="mt-10 flex flex-col gap-0.5 text-sm">
          <NavItem to="/app"         icon={<FolderKanban size={16} />}> Projects </NavItem>
          <NavItem to="/app/account" icon={<User size={16} />}>         Account  </NavItem>
          {user?.role === "admin" && (
            <NavItem to="/admin" icon={<ShieldCheck size={16} />}>      Admin    </NavItem>
          )}
          <ExternalNavItem
            href="https://realflow.so/docs"
            icon={<BookOpen size={16} />}
          >
            Docs
          </ExternalNavItem>
        </nav>

        <div className="mt-auto" />

        <ThemeToggle />
        <button
          onClick={logout}
          className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-ink/65 transition hover:bg-ink/4 hover:text-ink"
        >
          <LogOut size={16} /> Sign out
        </button>
      </aside>

      <main className="flex-1 px-6 py-10 md:px-10">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({ to, icon, children }: { to: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-ink/65 transition hover:bg-ink/4 hover:text-ink [&.active]:bg-ink/8 [&.active]:text-ink"
      activeOptions={{ exact: true }}
    >
      {icon} {children}
    </Link>
  );
}

function ExternalNavItem({ href, icon, children }: { href: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-ink/65 transition hover:bg-ink/4 hover:text-ink"
    >
      {icon} {children}
    </a>
  );
}

// Theme toggle — flips <html data-theme> and persists to the shared
// 'rf-theme' key (same mechanism as the marketing site). The pre-paint
// init in index.html applies it before React mounts.
function ThemeToggle() {
  const [theme, setTheme] = useState<string>(
    () => document.documentElement.getAttribute("data-theme") || "dark",
  );
  function toggle() {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("rf-theme", next);
    } catch {
      /* ignore */
    }
    setTheme(next);
  }
  return (
    <button
      onClick={toggle}
      aria-label="Toggle theme"
      className="mt-8 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-ink/65 transition hover:bg-ink/4 hover:text-ink"
    >
      {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
      {theme === "dark" ? "Light mode" : "Dark mode"}
    </button>
  );
}

// Suppress unused-import warning for icons we'll wire in in Phase 1.
void KeyRound; void BarChart3;
